import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { apiErrorMessage } from '../api/abfApi';
import { StatusChip } from '../components/StatusChip';
import { INK } from '../onboarding/review/reviewStyles';

/**
 * Slice 4 FR-4.9 — the report that gates retiring the legacy covenant engine.
 *
 * <p>The report has existed since the retirement shipped and had no screen: it was
 * reachable only by calling the endpoint, which meant the one control standing between
 * "the old engine is off" and "these facilities have nothing testing them" was invisible
 * to the people who would care. This is that screen.
 *
 * <p>It is an estate-level report, not a facility one. It is mounted on Monitoring because
 * that is where someone asking "is anything testing this?" already is, but it answers for
 * every ABF facility in the tenant and says so.
 *
 * <p>Loaded when the section is opened rather than with the workspace, since it is the same
 * answer on every facility and nobody should pay for it on a page view.
 */

const ROOT = '/api/credit/abf/round2';

const Wrap = styled.div`
  font-size: 13px; line-height: 1.55;
  h3 { margin: 16px 0 8px; font-size: 14px; color: ${INK.strong}; }
  p { margin: 0 0 10px; color: ${INK.muted}; }
`;

const Verdict = styled.p<{ $ready?: boolean }>`
  margin: 0 0 14px !important;
  padding: 10px 12px;
  border: 1px solid ${p => p.$ready ? 'rgba(52,211,153,.32)' : 'rgba(240,162,74,.34)'};
  border-radius: 9px;
  background: ${p => p.$ready ? 'rgba(6,78,59,.14)' : 'rgba(120,53,15,.16)'};
  color: ${p => p.$ready ? '#a7f3d0' : '#fcd9a8'} !important;
`;

const Counts = styled.dl`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
  gap: 1px; margin: 0 0 14px; border: 1px solid ${INK.line}; border-radius: 9px;
  background: ${INK.line}; overflow: hidden;
  > div { padding: 9px 12px; background: ${INK.panel}; }
  dt { color: ${INK.faint}; font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; }
  dd { margin: 4px 0 0; color: ${INK.strong}; font-size: 17px; font-weight: 800; }
  dd[data-tone='bad'] { color: #fca5a5; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12.5px;
  th { text-align: left; padding: 6px 8px; color: ${INK.faint}; font-size: 11px;
       font-weight: 800; letter-spacing: .05em; text-transform: uppercase;
       border-bottom: 1px solid ${INK.line}; }
  td { padding: 8px; border-bottom: 1px solid ${INK.line}; vertical-align: top; color: ${INK.body}; }
  tr:last-child td { border-bottom: 0; }
  small { display: block; color: ${INK.faint}; font-size: 11.5px; margin-top: 2px; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
`;

interface Props { open: boolean }

const LegacyRetirementReport: React.FC<Props> = ({ open }) => {
  const [report, setReport] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${ROOT}/covenants/legacy-comparison`, { params: { _: Date.now() } });
      setReport(data);
    } catch (e) {
      setError(apiErrorMessage(e, 'Unable to load the retirement report.'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open && !report && !loading) void load(); }, [open, report, loading, load]);

  if (error) return <Wrap><p style={{ color: '#fca5a5' }}>{error}</p></Wrap>;
  if (!report) return <Wrap><p>{loading ? 'Reading both engines…' : 'Open to run the comparison.'}</p></Wrap>;

  const uncovered: any[] = report.uncoveredActiveFacilities || [];
  const differing: any[] = (report.lines || []).filter((l: any) => l.status === 'DIFFERENT_VERDICT');

  return <Wrap>
    <p>Estate-level, not this facility. The earlier covenant engine can be retired only
      once results match, and "match" means the verdict, not the number: the two engines read
      different things on purpose.</p>

    <Verdict $ready={Boolean(report.retirementReady)}>{report.verdict}</Verdict>

    <Counts>
      <div><dt>Legacy definitions</dt><dd>{report.legacyDefinitions}</dd></div>
      <div><dt>Verdict agrees</dt><dd>{report.agree}</dd></div>
      <div><dt>Verdict differs</dt><dd data-tone={report.differentVerdict > 0 ? 'bad' : undefined}>{report.differentVerdict}</dd></div>
      <div><dt>No governed result</dt><dd>{report.noGovernedResult}</dd></div>
      <div><dt>Uncovered facilities</dt><dd data-tone={uncovered.length > 0 ? 'bad' : undefined}>{uncovered.length}</dd></div>
    </Counts>

    {uncovered.length > 0 && <>
      <h3>Facilities with nothing testing their covenants</h3>
      <p>These are active facilities where no governed covenant is defined. Each needs its
        agreement onboarding, or the gap accepting with an owner and a date against it.</p>
      <Table>
        <thead><tr><th>Facility</th><th className="num">Legacy definitions</th><th>Why</th></tr></thead>
        <tbody>{uncovered.map((f: any) => (
          <tr key={f.id}>
            <td><strong>{f.facility_name}</strong><small>#{f.id} · {f.status}</small></td>
            <td className="num">{f.legacy_definitions}</td>
            <td>{f.reason}</td>
          </tr>))}</tbody>
      </Table>
    </>}

    {differing.length > 0 && <>
      <h3>Covenants where the two engines disagree</h3>
      <Table>
        <thead><tr><th>Facility</th><th>Metric</th><th className="num">Legacy</th><th className="num">Governed</th><th>Why they differ</th></tr></thead>
        <tbody>{differing.map((l: any, i: number) => (
          <tr key={i}>
            <td>{l.facilityName}<small>{l.facilityCode}</small></td>
            <td>{l.metric}<small>{l.governedCovenantKey}</small></td>
            <td className="num">{String(l.legacyActual)}<small>{l.legacyVerdict}</small></td>
            <td className="num">{String(l.governedActual ?? '—')}<small>{l.governedVerdict ?? '—'}</small></td>
            <td>{l.explanation}</td>
          </tr>))}</tbody>
      </Table>
    </>}

    <h3>Every legacy definition</h3>
    <Table>
      <thead><tr><th>Facility</th><th>Metric</th><th>Legacy verdict</th><th>Governed verdict</th><th>Status</th></tr></thead>
      <tbody>{(report.lines || []).map((l: any, i: number) => (
        <tr key={i}>
          <td>{l.facilityName}<small>{l.facilityCode}</small></td>
          <td>{l.metric}<small>{l.governedCovenantKey || 'no governed equivalent'}</small></td>
          <td>{l.legacyVerdict}</td>
          <td>{l.governedVerdict || '—'}</td>
          <td><StatusChip
            tone={l.status === 'AGREE' ? 'done' : l.status === 'DIFFERENT_VERDICT' ? 'critical' : 'neutral'}
            label={l.status === 'AGREE' ? 'Agree'
              : l.status === 'DIFFERENT_VERDICT' ? 'Differs' : 'No governed result'} />
          </td>
        </tr>))}</tbody>
    </Table>
    <p style={{ marginTop: 10, color: INK.faint }}>
      As of {String(report.asOf)}. The legacy branch reads the borrower's own header figures
      off the facility row; the governed engine computes from the released tape and the
      approved base and freezes its inputs. Numbers are expected to differ; verdicts are not.
    </p>
  </Wrap>;
};

export default LegacyRetirementReport;
